import React, { useState, useEffect } from 'react';
import api from '../api';
import '../pages/CardCreator.css';
import { sanitizeFilename } from './CanvasExportButton';

// --- Group detection utility ---
function detectCardGroups(cards) {
  const visited = new Set();
  const groups = [];
  const cardMap = new Map(cards.map(card => [`${card.pos_x},${card.pos_y}`, card]));
  const directions = [[0, 1], [1, 0], [0, -1], [-1, 0]];

  for (const card of cards) {
    const key = `${card.pos_x},${card.pos_y}`;
    if (visited.has(key)) continue;
    const group = [];
    const queue = [card];
    visited.add(key);
    while (queue.length) {
      const current = queue.shift();
      group.push(current);
      for (const [dx, dy] of directions) {
        const nx = current.pos_x + dx;
        const ny = current.pos_y + dy;
        const nKey = `${nx},${ny}`;
        if (cardMap.has(nKey) && !visited.has(nKey)) {
          visited.add(nKey);
          queue.push(cardMap.get(nKey));
        }
      }
    }
    groups.push(group);
  }
  return groups;
}

// --- Symbol removal utility ---
function removeSymbols(text) {
  if (!text) return '';
  return text.replace(/(\s*\{\{symbol:[^}]+\}\}\s*([/+]|and)?\s*)+/gi, '').trim();
}

// Helper to get card type name
function getTypeName(cardType, language) {
  return (
    cardType?.translations?.[language]?.name ||
    cardType?.translations?.en?.name ||
    cardType?.name ||
    'Unknown'
  );
}

const EXPORT_INTRO_BLOCKS = {
  en: `You are a Narrative Architect.
Your task is to transform a "Narrative Blueprint" (extracted from a visual story canvas) into a compelling, cohesive story.

GUIDELINES:
1. THE STRUCTURE: The blueprint organizes elements into "Groups". Each Group is an Entity.
2. SPATIAL MEANING: Within a Group, the order of elements (Top-to-Bottom, Left-to-Right) conveys a logical sequence or a hierarchy of importance. Treat the first elements as the "Core" of the entity and subsequent ones as details, modifiers, or secondary traits.
3. THE LOGIC: The "Relations" between Groups define the causality, interaction, and narrative timeline. Follow these connections strictly.
4. THE DEPTH: Sub-Canvases provide nested detail or sub-plots. Integrate this depth naturally.
5. THE FORMAT: Do not just list the components. Write a fluent, imaginative narrative. Use card types {in curly braces} for context only.`,
  nl: `Je bent een Verhaalarchitect.
Het is jouw taak om een "Narrative Blueprint" (geëxtraheerd uit een visueel verhaalcanvas) te transformeren naar een meeslepend, samenhangend verhaal.

RICHTLIJNEN:
1. DE STRUCTUUR: De blauwdruk organiseert elementen in "Groepen". Elke Groep is een Entiteit.
2. RUIMTELIJKE BETEKENIS: Binnen een groep geeft de volgorde van elementen (van boven naar beneden, van links naar rechts) een logische volgorde of een hiërarchie van belang aan. Behandel de eerste elementen als de "Kern" van de entiteit en de daaropvolgende als details, modifiers of secundaire eigenschappen.
3. DE LOGICA: De "Relaties" tussen groepen bepalen de causaliteit, interactie en de narratieve tijdlijn. Volg deze verbindingen strikt.
4. DE DIEPTE: Sub-Canvassen bieden geneste details of subplots. Integreer deze diepgang op een natuurlijke wijze.
5. HET FORMAAT: Maak niet alleen een lijst van de componenten. Schrijf een vloeiend, fantasierijk verhaal. Gebruik kaarttypes {tussen accolades} alleen voor de context.`
};

const StoryExportPanel = ({ canvasId, canvasName, onClose, cards = [], relations = [], language = 'en', cardTypes = [], decks = [] }) => {
  const [error, setError] = useState('');
  const [resolvedCanvases, setResolvedCanvases] = useState({});
  const [isLoadingReferences, setIsLoadingReferences] = useState(false);

  const typeMap = {};
  cardTypes.forEach(type => { typeMap[type.type_id] = type; });

  const groups = detectCardGroups(cards);

  const getCardText = (card) => {
    if (card.type_id === 0) return card.user_text || '';
    const textObj = (card.texts || []).find(
      t => t.language === language && t.option_index === (card.selected_option_index || 1)
    );
    return textObj ? textObj.text : '';
  };

  useEffect(() => {
    const fetchReferences = async () => {
      setIsLoadingReferences(true);
      const visited = new Set([Number(canvasId)]);
      const resolved = {};

      const resolve = async (currentCards) => {
        const canvasRefs = currentCards
          .map(c => {
            const match = (c.user_text || "").match(/\{Canvas:(\d+)\}/);
            return match ? Number(match[1]) : null;
          })
          .filter(id => id && !visited.has(id));

        for (const id of canvasRefs) {
          visited.add(id);
          try {
            const res = await api.get(`/api/canvas/${id}`);
            if (res.data) {
              const canvasData = {
                name: res.data.canvas?.name || `Canvas ${id}`,
                cards: res.data.cards || [],
                relations: res.data.relations || [],
                groups: detectCardGroups(res.data.cards || [])
              };
              resolved[id] = canvasData;
              await resolve(canvasData.cards);
            }
          } catch (err) {
            console.error(`Failed to resolve canvas ${id}:`, err);
          }
        }
      };

      await resolve(cards);
      setResolvedCanvases(resolved);
      setIsLoadingReferences(false);
    };

    fetchReferences();
  }, [canvasId, cards]);

  function buildCanvasPrompt(currentName, currentCards, currentRelations, currentGroups, depth = 0) {
    const indent = "  ".repeat(depth);
    const subIndent = "  ".repeat(depth + 1);
    const localLines = [];

    localLines.push(`${indent}# CANVAS: ${currentName}`);
    localLines.push("");

    const resolveLabel = (card) => {
      const rawText = getCardText(card);
      const match = (rawText || "").match(/\{Canvas:(\d+)\}/);
      if (match) {
        const subId = Number(match[1]);
        return resolvedCanvases[subId]?.name ? `Sub-Canvas [${resolvedCanvases[subId].name}]` : `Canvas ${subId}`;
      }
      return removeSymbols(rawText);
    };

    // Groups as Entities
    currentGroups.forEach((group, idx) => {
      localLines.push(`${subIndent}ENTITY ${idx + 1}:`);

      // Sort group cards spatially: Top-to-Bottom, then Left-to-Right
      const sortedGroup = [...group].sort((a, b) => (a.pos_y - b.pos_y) || (a.pos_x - b.pos_x));

      sortedGroup.forEach(card => {
        const typeName = card.type_id === 0
          ? (language === 'nl' ? 'Gebruikersinvoer' : 'User input')
          : getTypeName(typeMap[card.type_id], language);

        const label = resolveLabel(card);
        const match = (card.user_text || "").match(/\{Canvas:(\d+)\}/);

        if (match) {
          const subId = Number(match[1]);
          const subCanvas = resolvedCanvases[subId];
          if (subCanvas) {
            localLines.push(`${subIndent}  - {${typeName}} Reference to ${label}`);
            localLines.push("");
            localLines.push(...buildCanvasPrompt(subCanvas.name, subCanvas.cards, subCanvas.relations, subCanvas.groups, depth + 2));
            localLines.push("");
          } else {
            localLines.push(`${subIndent}  - {${typeName}} Reference to ${label}`);
          }
        } else {
          localLines.push(`${subIndent}  - {${typeName}} ${label}`);
        }
      });
      localLines.push("");
    });

    // Relations as Timeline/Logic
    if (currentRelations.length) {
      localLines.push(`${subIndent}RELATIONS (Narrative Logic & Flow):`);
      const cardToGroupMap = {};
      currentGroups.forEach((group, gIdx) => {
        group.forEach(c => cardToGroupMap[c.canvas_card_id] = gIdx + 1);
      });

      currentRelations.forEach(rel => {
        const startCard = currentCards.find(c => c.canvas_card_id === rel.start?.canvasCardId);
        const endCard = currentCards.find(c => c.canvas_card_id === rel.end?.canvasCardId);

        const startLabel = startCard ? (resolveLabel(startCard) || `Entity ${cardToGroupMap[startCard.canvas_card_id]}`) : "?";
        const endLabel = endCard ? (resolveLabel(endCard) || `Entity ${cardToGroupMap[endCard.canvas_card_id]}`) : "?";

        localLines.push(`${subIndent}  - ${startLabel} --[${rel.type || "relates to"}]--> ${endLabel}`);
      });
      localLines.push("");
    }
    return localLines;
  }

  const exportTextLines = [];
  const introBlock = EXPORT_INTRO_BLOCKS[language] || EXPORT_INTRO_BLOCKS['en'];
  exportTextLines.push(introBlock.trim());
  exportTextLines.push("");
  if (!isLoadingReferences) {
    exportTextLines.push(...buildCanvasPrompt(canvasName, cards, relations, groups, 0));
  } else {
    exportTextLines.push("... Loading sub-canvas data and resolving references ...");
  }

  const exportText = exportTextLines.join("\n");

  const handleDownload = async () => {
    try {
      const blob = new Blob([exportText], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const safeName = sanitizeFilename(canvasName) || 'storyweaver-canvas';
      link.download = `${safeName}_prompt.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) { setError('Failed to download export'); }
  };

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(exportText);
      setError('');
    } catch (err) {
      try {
        const textarea = document.querySelector('textarea[readonly]');
        if (textarea) { textarea.select(); document.execCommand('copy'); setError(''); return; }
      } catch (fallbackErr) { }
      setError('Failed to copy to clipboard');
    }
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0, 0, 0, 0.7)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{
        maxWidth: '800px',
        maxHeight: '90vh',
        width: '90vw',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        background: 'rgba(15, 23, 42, 0.8)',
        backdropFilter: 'blur(24px)',
        WebkitBackdropFilter: 'blur(24px)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '24px',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
        padding: '24px',
        color: '#f8fafc'
      }}>
        <div style={{ marginBottom: '20px', borderBottom: '1px solid rgba(255, 255, 255, 0.1)', paddingBottom: '16px', position: 'relative' }}>
          <h2 style={{ margin: 0, color: '#f8fafc', paddingRight: '40px', fontSize: '24px', fontWeight: '700' }}>Export Story Prompt for AI</h2>
          <button onClick={onClose} style={{ position: 'absolute', top: '0', right: '0', background: 'none', border: 'none', color: 'rgba(255, 255, 255, 0.5)', fontSize: '28px', cursor: 'pointer', padding: '2px 10px', borderRadius: '4px', lineHeight: '1', zIndex: 2 }}>×</button>
        </div>
        {error && <div className="error" style={{ marginBottom: '16px' }}>{error}</div>}
        <div style={{ flex: 1, marginBottom: '20px' }}>
          <label style={{ color: 'rgba(255, 255, 255, 0.7)', marginBottom: '8px', display: 'block', fontWeight: '500', fontSize: '14px' }}>AI Prompt Preview:</label>
          <textarea
            value={exportText}
            readOnly
            style={{
              background: 'rgba(0, 0, 0, 0.2)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '12px',
              padding: '16px',
              height: '300px',
              width: '100%',
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: '13px',
              color: '#e2e8f0',
              whiteSpace: 'pre',
              lineHeight: '1.6',
              resize: 'vertical',
              boxSizing: 'border-box',
              outline: 'none'
            }}
          />
        </div>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', borderTop: '1px solid rgba(255, 255, 255, 0.1)', paddingTop: '16px' }}>
          <button onClick={handleCopyToClipboard} style={{
            padding: '10px 20px',
            background: 'rgba(255, 255, 255, 0.05)',
            color: '#f8fafc',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '12px',
            cursor: 'pointer',
            fontWeight: '600',
            transition: 'all 0.2s'
          }}>Copy to Clipboard</button>
          <button onClick={handleDownload} style={{
            padding: '10px 20px',
            background: '#6366f1',
            color: '#fff',
            border: 'none',
            borderRadius: '12px',
            cursor: 'pointer',
            fontWeight: '600',
            transition: 'all 0.2s',
            boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)'
          }}>Download File</button>
        </div>
        <div style={{ marginTop: '16px', padding: '12px', background: 'rgba(99, 102, 241, 0.1)', borderRadius: '12px', fontSize: '13px', color: '#818cf8', border: '1px solid rgba(99, 102, 241, 0.2)' }}>
          <strong>How to use:</strong><br />1. Copy or download the prompt<br />2. Paste into AI service<br />3. Generate your story!
        </div>
        <div style={{ marginTop: '16px', fontSize: '10px', color: '#999' }}>
          <div>Canvas: {canvasName} ({language}) | Entities: {groups.length}</div>
        </div>
      </div>
    </div>
  );
};

export default StoryExportPanel;